/**
 * Invoice Export Utility
 * Generates Excel files for TG invoice data export
 */

import * as XLSX from 'xlsx'

export interface InvoiceExportAssignment {
  id: string
  assignment_number: number | null
  title: string
  start_date: string
  location: string | null
  report_text: string | null
  reported_articles: ReportedArticleExport[] | null
}

export interface ReportedArticleExport {
  article_id: string | null  // null för utlägg
  article_name: string
  actual_quantity: number | null
  unit: string | null
  unit_price: number | null
  notes?: string
  // Utlägg-fält
  is_expense?: boolean
  expense_amount_incl_vat?: number | null
  expense_amount_excl_vat?: number | null
  expense_vat_rate?: number | null
}

/**
 * Generate Excel workbook for invoice export
 */
export function generateInvoiceExcel(assignments: InvoiceExportAssignment[]): XLSX.WorkBook {
  // Column headers
  const headers = ['Uppdragsnr', 'Titel', 'Datum', 'Plats', 'Artikel', 'Antal', 'Enhet', 'Á-pris ex moms', 'Summa ex moms', 'Anteckningar', 'Beskrivning för faktura']

  // Build all rows (including headers)
  const allRows: (string | number | null)[][] = [headers]

  for (const assignment of assignments) {
    const assignmentId = assignment.assignment_number ? `#${assignment.assignment_number}` : ''
    const baseData = [
      assignmentId,
      assignment.title,
      assignment.start_date,
      assignment.location || ''
    ]

    const articles = assignment.reported_articles || []

    // Add report text as separate row FIRST (before articles)
    if (assignment.report_text) {
      const invoiceDesc = [assignmentId, assignment.start_date, assignment.location || ''].filter(Boolean).join(', ')
      allRows.push([
        ...baseData,
        'Övrig anteckning', // Artikel
        '', // Antal
        '', // Enhet
        '', // Á-pris ex moms
        '', // Summa ex moms
        assignment.report_text, // Anteckningar (rapportanteckning)
        invoiceDesc // Beskrivning för faktura
      ])
    }

    // Separate regular articles from expenses
    const regularArticles = articles.filter(a => !a.is_expense)
    const expenses = articles.filter(a => a.is_expense)

    // Add regular article rows
    regularArticles.forEach((article) => {
      const quantity = article.actual_quantity || 0
      const unitPrice = article.unit_price || 0
      const lineTotal = quantity * unitPrice

      // Build invoice description: id - artikelbeskrivning, datum, adress
      const invoiceDesc = [
        assignmentId ? `${assignmentId} - ${article.article_name}` : article.article_name,
        assignment.start_date,
        assignment.location || ''
      ].filter(Boolean).join(', ')

      allRows.push([
        ...baseData,
        article.article_name,
        article.actual_quantity,
        article.unit || '',
        article.unit_price,
        lineTotal > 0 ? lineTotal : '',
        article.notes || '', // Anteckningar (artikelanteckning)
        invoiceDesc // Beskrivning för faktura
      ])
    })

    // Add expense rows
    expenses.forEach((expense) => {
      const amount = expense.expense_amount_excl_vat || 0

      // Build invoice description
      const invoiceDesc = [
        assignmentId ? `${assignmentId} - Utlägg: ${expense.article_name}` : `Utlägg: ${expense.article_name}`,
        assignment.start_date,
        assignment.location || ''
      ].filter(Boolean).join(', ')

      allRows.push([
        ...baseData,
        `Utlägg: ${expense.article_name}`,
        1, // Antal alltid 1 för utlägg
        'st',
        amount, // Pris ex moms
        amount, // Summa = pris (antal alltid 1)
        expense.expense_vat_rate !== null ? `Moms: ${expense.expense_vat_rate}%` : '', // Anteckningar
        invoiceDesc // Beskrivning för faktura
      ])
    })

    // If no articles and no report text - still add one row for the assignment
    if (articles.length === 0 && !assignment.report_text) {
      const invoiceDesc = [assignmentId, assignment.title, assignment.start_date, assignment.location || ''].filter(Boolean).join(', ')
      allRows.push([
        ...baseData,
        '', // Artikel
        '', // Antal
        '', // Enhet
        '', // Á-pris ex moms
        '', // Summa ex moms
        '', // Anteckningar
        invoiceDesc // Beskrivning för faktura
      ])
    }

  }

  // Calculate total sum of all "Summa ex moms" values
  const grandTotal = allRows.slice(1).reduce((sum, row) => {
    const val = row[8]
    return sum + (typeof val === 'number' ? val : 0)
  }, 0)

  // Add separator and total row
  allRows.push(['', '', '', '', '', '', '', '', '', '', ''])
  allRows.push(['', '', '', '', 'TOTALT ex moms', '', '', '', grandTotal, '', ''])

  // Create worksheet from array of arrays
  const worksheet = XLSX.utils.aoa_to_sheet(allRows)

  // Apply number format to price columns (H = Á-pris, I = Summa)
  const priceFormat = '#,##0.00 "kr"'
  for (let row = 1; row < allRows.length; row++) {
    const cellH = XLSX.utils.encode_cell({ r: row, c: 7 }) // Á-pris ex moms
    const cellI = XLSX.utils.encode_cell({ r: row, c: 8 }) // Summa ex moms

    if (worksheet[cellH] && typeof worksheet[cellH].v === 'number') {
      worksheet[cellH].z = priceFormat
    }
    if (worksheet[cellI] && typeof worksheet[cellI].v === 'number') {
      worksheet[cellI].z = priceFormat
    }
  }

  // Set column widths
  worksheet['!cols'] = [
    { wch: 12 },  // Uppdragsnr
    { wch: 30 },  // Titel
    { wch: 12 },  // Datum
    { wch: 25 },  // Plats
    { wch: 20 },  // Artikel
    { wch: 8 },   // Antal
    { wch: 8 },   // Enhet
    { wch: 14 },  // Á-pris ex moms
    { wch: 14 },  // Summa ex moms
    { wch: 40 },  // Anteckningar
    { wch: 50 }   // Beskrivning för faktura
  ]

  // Create workbook
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Faktureringsunderlag')

  return workbook
}

/**
 * Download Excel file
 */
export function downloadExcel(workbook: XLSX.WorkBook, filename: string): void {
  XLSX.writeFile(workbook, filename)
}
